// Image optimization utilities
// This file provides helper functions for optimizing images

/**
 * Generates a responsive image URL with proper sizing parameters
 * @param url Original image URL (must be from Pexels or similar service that supports parameters)
 * @param width Desired width
 * @param height Desired height (optional)
 * @param quality Image quality (1-100, default 80)
 * @returns Optimized image URL
 */
export const getOptimizedImageUrl = (
  url: string,
  width: number,
  height?: number,
  quality: number = 80
): string => {
  // Handle local images
  if (url.startsWith('/')) {
    return url;
  }
  
  // Only process URLs we know support parameters
  if (!url.includes('pexels.com') && !url.includes('images.unsplash.com')) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    
    // Handle Pexels images
    if (url.includes('pexels.com')) {
      // Remove any existing auto parameter
      urlObj.searchParams.delete('auto');
      
      // Add optimization parameters
      urlObj.searchParams.set('auto', 'compress');
      urlObj.searchParams.set('cs', 'tinysrgb');
      urlObj.searchParams.set('w', width.toString());
      
      if (height) {
        urlObj.searchParams.set('h', height.toString());
        urlObj.searchParams.set('fit', 'crop');
      }
      
      urlObj.searchParams.set('q', quality.toString());
      
      return urlObj.toString();
    }
    
    // Handle Unsplash images
    if (url.includes('images.unsplash.com')) {
      // Add optimization parameters
      urlObj.searchParams.set('w', width.toString());
      
      if (height) {
        urlObj.searchParams.set('h', height.toString());
      }
      
      urlObj.searchParams.set('q', quality.toString());
      urlObj.searchParams.set('auto', 'format');
      urlObj.searchParams.set('fit', 'crop');
      
      return urlObj.toString();
    }
    
    return url;
  } catch (error) {
    console.error('Error optimizing image URL:', error);
    return url;
  }
};

/**
 * Creates a responsive srcSet for images
 * @param url Original image URL
 * @param widths Array of widths to generate
 * @param quality Image quality (1-100, default 80)
 * @returns srcSet string
 */
export const generateSrcSet = (
  url: string,
  widths: number[] = [320, 640, 960, 1280],
  quality: number = 80
): string => {
  // Handle local images
  if (url.startsWith('/')) {
    return '';
  }
  
  if (!url.includes('pexels.com') && !url.includes('images.unsplash.com')) {
    return '';
  }

  try {
    return widths
      .map(width => `${getOptimizedImageUrl(url, width, undefined, quality)} ${width}w`)
      .join(', ');
  } catch (error) {
    console.error('Error generating srcSet:', error);
    return '';
  }
};

/**
 * Determines if an image should be preloaded based on its position
 * @param url Image URL
 * @param isHero Whether the image is a hero image
 * @returns Boolean indicating if image should be preloaded
 */
export const shouldPreloadImage = (url: string, isHero: boolean = false): boolean => {
  // Always preload hero images
  if (isHero) return true;
  
  // Don't preload local images (they're likely already optimized)
  if (url.startsWith('/')) return false;
  
  // Don't preload images from unknown sources
  if (!url.includes('pexels.com') && !url.includes('images.unsplash.com')) {
    return false;
  }
  
  return false;
};

/**
 * Generates a low-quality image placeholder URL
 * @param url Original image URL
 * @returns Low quality placeholder URL
 */
export const getLowQualityPlaceholder = (url: string): string => {
  if (url.startsWith('/') || (!url.includes('pexels.com') && !url.includes('images.unsplash.com'))) {
    return url;
  }
  
  try {
    return getOptimizedImageUrl(url, 20, undefined, 20);
  } catch (error) {
    console.error('Error generating low quality placeholder:', error);
    return url;
  }
};